<template>
    <div
        v-if="cue && cue.data"
        class="ql-cue-header-shell"
        :class="{ 'ql-cue-header-shell--noteslist': notesListLinked }">
        <md-toolbar
            class="md-dense ql-cue-header"
            :class="{
                'ql-cue-worknote-marked': cue.data && isActiveCue
            }"
            ref="ql-cue-header">
            <div
                class="ql-cue-layer-func"
            >
                <div
                    class="ql-cue-layer-func-back"
                    @click="toggle"
                    :class="cuePlacementclass(cue)"
                    @contextmenu="preventContextFunc($event)"
                    :style="{backgroundColor: layer.color}"
                ></div>
                <md-tooltip md-direction="top">{{ headerClickTooltip }}</md-tooltip>
            </div>
            <div class="cue-header-title" :class="{'cue-header-both': [headerBox.title, headerBox.label, headerBox.time].filter(Boolean).length >= 2}" :id="'cue-header-title-' + cue.id" :style="cueHeaderTitleStyles" @dblclick="selectWordInField">
                <layer-picker
                    :currentLayer="layer"
                    :layers="layers"
                    :editable="editable"
                    @selectLayer="selectLayer"
                    :locked="locked"
                    :cue="cue"
                    :titleFontSize="titleFontSize"
                    :layerCueLineOpacity="cueLineOpacity"
                    :layerCueBoxOpacity="cueBoxOpacity"
                    :headerBox="headerBox"
                    :svgTrueHeight="svgTrueHeight"
                    @toggle="toggle"
                    @focusWorkNote="focusInputWorkNote"
                />
                <div class="cue-header-separator" :class="{'cue-header-separator--empty': headerBox.layerName === false}"></div>
                <!-- Each field swaps for its own input in place (Josh, 11 Oct 2026): the
                     others stay put, and the input takes the field's size, so nothing moves. -->
                <template v-if="headerBox.title">
                    <div class="cue-header-cue-title" v-if="!isEditing" :id="'cue-header-number-' + cue.id" @click="edit" @touchstart="edit" :class="{'placeholder': !cue.title, 'short-box': cue.title.length <= 12}" :style="titleStyle">
                        {{cue.title || 'Cue Number'}}
                        <md-tooltip md-direction="top" v-if="editable && !isEditing && !isEditingDescription">Click to edit</md-tooltip>
                    </div>
                    <form novalidate @submit.stop.prevent="submit" v-else :style="titleStyle" :class="showMarginView ? 'margin-view' : ''" v-on:keyup.stop.prevent="keyPress" v-on:keydown="keyDown">
                        <md-input-container class="cue-header-title-edit">
                            <md-input :value="cue.title" @blur="$value => modifyCue('title', $value, true)" ref="input_cue_title" placeholder="Cue Number"></md-input>
                            <md-tooltip md-direction="top">Enter to save, Escape to cancel</md-tooltip>
                        </md-input-container>
                    </form>
                </template>
                <div class="cue-header-separator" v-if="headerBox.title && headerBox.label"></div>
                <template v-if="headerBox.label">
                    <div class="cue-header-cue-description" v-if="!isEditingDescription" :id="'cue-header-label-' + cue.id" @click="editDescription" :class="{'placeholder': !cueLabel, 'short-box': cueLabel.length <= 12}" :style="titleStyle">
                        {{cueLabel || 'Cue Label'}}
                        <md-tooltip md-direction="top" v-if="editable && !isEditing && !isEditingDescription">Click to edit</md-tooltip>
                    </div>
                    <form novalidate @submit.stop.prevent="submitDescription" :class="showMarginView ? 'margin-view' : ''" v-else :style="titleStyle" v-on:keyup.stop.prevent="keyPressDescription" v-on:keydown="keyDownDescription">
                        <md-input-container class="cue-header-description-edit">
                            <md-input :value="cue.data.label" @blur="$value => modifyCue('data.label', $value, true)" ref="input_cue_description" placeholder="Cue Label"></md-input>
                            <md-tooltip md-direction="top">Enter to save, Escape to cancel</md-tooltip>
                        </md-input-container>
                    </form>
                </template>
                <div class="cue-header-separator" v-if="(headerBox.title || headerBox.label) && headerBox.time"></div>
                <template v-if="headerBox.time">
                    <div class="cue-header-cue-time" v-if="!isEditingTime" :id="'cue-header-time-' + cue.id" @click="editTime" @touchstart="editTime" :class="{'placeholder': !cue.data.time, 'short-box': (cue.data.time || '').length <= 12}" :style="titleStyle">
                        {{cue.data.time || 'Cue Time'}}
                        <md-tooltip md-direction="top" v-if="editable && !isEditing && !isEditingDescription && !isEditingTime">Click to edit</md-tooltip>
                    </div>
                    <form novalidate @submit.stop.prevent="submitTime" :class="showMarginView ? 'margin-view' : ''" v-else :style="titleStyle" v-on:keyup.stop.prevent="keyPressTime" v-on:keydown="keyDownTime">
                        <md-input-container class="cue-header-time-edit">
                            <md-input :value="cue.data.time" @blur="$value => modifyCue('data.time', $value, true)" ref="input_cue_time" placeholder="Cue Time"></md-input>
                            <md-tooltip md-direction="top">Enter to save, Escape to cancel</md-tooltip>
                        </md-input-container>
                    </form>
                </template>
                <!-- Phosphor Icons "asterisk", bold weight (MIT). Chosen over the Material and
                     Lucide asterisks 29 Aug 2026: six rounded arms read warmer than an
                     eight-arm grid mark, and the bold weight is what survives the size this
                     actually renders at — max(titleFontSize - 4, 8), i.e. 8-16px. Do not
                     swap to Phosphor's `fill` weight: that is a solid disc with the asterisk
                     knocked out, not a heavier mark. -->
                <svg class="ql-cue-worknote-marker" :class="{ 'ql-cue-worknote-marker--noteslist': worknoteFromNotesList }" :style="worknoteStyles" viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path fill="currentColor" d="M218.29,182.17a12,12,0,0,1-16.47,4.12L140,149.19V216a12,12,0,0,1-24,0V149.19l-61.82,37.1a12,12,0,1,1-12.35-20.58L104.68,128,41.83,90.29A12,12,0,1,1,54.18,69.71L116,106.81V40a12,12,0,0,1,24,0v66.81l61.82-37.1a12,12,0,1,1,12.35,20.58L151.32,128l62.85,37.71A12,12,0,0,1,218.29,182.17Z"/></svg>
            </div>

            <div class="cue-header-side-actions" v-if="isFloatingCue || editable">
                <template v-if="isFloatingCue">
                    <div class="button-container" @contextmenu="preventContext($event)" @touchstart="checkTwoFingerTap" :style="tetherWrapperStyle">
                        <span class="move-button drag fill-button-retether">
                            <div class="fill-button" :style="tetherStyle"></div>
                            <md-tooltip>Click and drag to move cue. Right click to re-tether.</md-tooltip>
                        </span>
                    </div>
                </template>
                <template v-else>
                    <div class="button-container" v-if="editable" @click="untetherCue" :style="tetherWrapperStyle">
                        <span class="move-button drag">
                            <div class="fill-button" :style="tetherStyle"></div>
                            <md-tooltip>Click to untether cue</md-tooltip>
                        </span>
                    </div>
                </template>
            </div>
        </md-toolbar>
        <button
            v-if="notesListLinked"
            type="button"
            class="noteslist-add-note-outside"
            :style="noteslistAddNoteStyles"
            aria-label="Add Note in The Notes List"
            @click.stop="openAddNote"
            @mousedown.stop
            @touchstart.stop>
            <img class="noteslist-add-note-icon" :src="noteslistNoteIconSrc" alt="" />
            <img class="noteslist-add-note-icon noteslist-add-note-icon--hover" :src="noteslistNoteIconHoverSrc" alt="" />
            <md-tooltip md-direction="top">Add Note in The Notes List</md-tooltip>
        </button>
    </div>
</template>

<script>
import { workNoteOf } from '@/lib/worknotes';
import { cueLabelOf, cueTimeOf } from '@/lib/cue-fields';
import { mapGetters } from 'vuex';
import NavBus from '@/buses/nav';
import ProjectCuesLayerPicker from '@/components/project/cues/layer-picker';
import { NOTESLIST_INTEGRATION_ENABLED } from '@/lib/URLs';
import notesListOpenCount from '@/lib/noteslist-note-counts';
import noteslistNoteIcon from '@/assets/img/noteslist-note-icon.svg';
import noteslistNoteIconHover from '@/assets/img/noteslist-note-icon-hover.svg';
import { eventPoint, caretOffsetAt, wordBounds } from '@/lib/cue-caret';
import openNotesListAddNote from '@/lib/noteslist-add-note';

// Size of the Notes List "Add Note" icon beside a linked cue, and the gap
// between the cue and it. The icon sits OUTSIDE the cue box, so unlike the
// worknote star it is not capped by the toolbar's line height and can be
// whatever size reads best.
// The cue reserves NO width for this (see .ql-cue-header-shell--noteslist): the
// icon sits entirely outside the cue's right edge, so a linked cue is exactly as
// wide as an unlinked one. That relies on .ql-cue--noteslist-linked keeping
// overflow: visible in cue.vue.
// The Notes List "Add Note" icon is sized in CSS to the full height of the cue
// box (height: 100% against .ql-cue-header-shell, which is the cue's own
// height) — so it tracks the cue size the user sets in the layers menu or
// per-cue, with no ratio to keep in sync and nothing to re-tune if cue height
// ever changes. Only the gap between cue and icon is computed, so it stays
// proportional at small cue sizes.
const NOTESLIST_GAP_RATIO = 4 / 14;
const NOTESLIST_GAP_MIN = 2;
// The mark's own proportions (viewBox 32.69 x 29.61) — width follows height.
const NOTESLIST_ICON_ASPECT = 32.69 / 29.61;
// Floor for the icon. The cue header is auto-height, so a measurement can come
// back 0 before layout settles; without a floor the icon renders at zero size
// and simply disappears. Never let that happen.
const NOTESLIST_ICON_MIN = 12;
// How large the icon is relative to the cue box. cue.vue sizes a cue to
// (titleFontSize + 2) — 16px at the default — so 1.0 here means "exactly the
// cue's height", which reads small. The icon sits outside the box and the cue
// keeps overflow visible, so nothing constrains it. THIS IS THE DIAL: change
// only this to make the icon bigger or smaller at every cue size.
const NOTESLIST_ICON_SCALE = 1.15;


// Gap between the cue title and the tether/move handle at the cue's right end.
// Purely decorative spacing — the handle itself keeps its own 16px hit area
// (.cue-header-side-actions min-width). Was 12; halved so a cue carries less
// dead space on the right. Scales down with the cue's font size on the curve
// below, floored so it never closes up entirely.
// NOTE: .cue-header-title's `margin` and `max-width` in the stylesheet must
// match this — they are the pre-inline-style fallback.
const CUE_TITLE_GAP = 6;
const CUE_TITLE_GAP_MIN = 2;

// Distance the note marker keeps from the end of the cue title, at EVERY title
// size. It used to be implicit and wrong: the space reserved for the marker was a
// flat 13px while the marker itself scales 8 -> 20px with the title, so the gap
// drifted from 7px at the smallest title to MINUS 1px at 24 -- the mark overlapped
// the text at full size. Reserve (marker + this) instead of a constant.
const WORKNOTE_MARKER_GAP = 4;

/**
 * Component For...
 */
const ProjectCuesCueHeader = {
    name: 'projectCuesCueHeader',
    props: [
        'cue',
        'expanded',
        'layer',
        'layers',
        'locked',
        'editable',
        'color',
        'isEditing',
        'isEditingDescription',
        'isEditingTime',
        'isFloatingCue',
        'svgContentWidth',
        'svgTrueHeight'
    ],
    data: function() {
        return {
            oldVal: this.$props.cue.title,
            oldDescriptionVal: cueLabelOf(this.$props.cue),
            oldTimeVal: cueTimeOf(this.$props.cue),
            updateCueTimerId: null,
            lastClickCount: 0,
            // Measured height of the cue header, used to size the Add Note icon
            // to the cue box. 0 until measured; the getter floors it.
            noteslistMeasuredHeight: 0
        };
    },
    computed: {
        ...mapGetters([
            'user',
            'userProjectData',
            'headerBoxOptions',
            'titleFontSizeOptions',
            'cueLineOpacityOptions',
            'cueBoxOpacityOptions',
            'layerByCue',
            'getCueLineOpacity',
            'getLayerBoxOpacity',
            'getDefaultCuePlacement',
            'selectedScale',
            'showMarginView',
            'project',
            'notesListCounts'
        ]),
        notesListLinked() {
            // !! so this is a Boolean, not the production id string. It binds
            // straight to Boolean props (:disabled), which warn on a String.
            return !!(NOTESLIST_INTEGRATION_ENABLED
                && this.project
                && this.project.noteslist_link_status === 'linked'
                && this.project.noteslist_production_id);
        },
        headerClickTooltip() {
            if (this.notesListLinked) {
                return 'Left click to open attributes. Work notes are managed in The Notes List.';
            }
            return 'Left click to open attributes, right click to add work note';
        },
        // Old cues can lack data.label (21 Oct 2026): read it through cueLabelOf so the header still renders.
        cueLabel() {
            return String(cueLabelOf(this.cue));
        },
        headerBox() {
            const layers = this.userProjectData.layers;
            let layerDefault;
            if (layers && layers[this.layer.id] && layers[this.layer.id].headerBox) {
                layerDefault = { ...layers[this.layer.id].headerBox };
            } else if (this.layer.headerBox) {
                layerDefault = { ...this.layer.headerBox };
            } else {
                layerDefault = {
                    title: true,
                    label: true,
                    time: false
                };
            }
            // Legacy layers/cues stored a headerBox without `layerName`; a missing value
            // means "shown", so this feature is off by default for existing data.
            if (layerDefault.layerName === undefined) {
                layerDefault.layerName = true;
            }
            // A per-cue override wins only for the keys it explicitly contains; any key it
            // omits (e.g. `time` on cues created before Time existed) falls back to the layer's
            // current global default, so later global setting changes still reach existing cues.
            if (this.headerBoxOptions[this.cue.id]) {
                return { ...layerDefault, ...this.headerBoxOptions[this.cue.id] };
            }
            return layerDefault;
        },
        titleFontSize() {
            if (this.titleFontSizeOptions[this.cue.id]) {
                return this.titleFontSizeOptions[this.cue.id];
            }
            if (this.cue.titleFontSize) {
                return this.cue.titleFontSize;
            }

            const layers = this.userProjectData.layers;
            if (layers && layers[this.layer.id]) {
                if (layers[this.layer.id].titleFontSize) {
                    return layers[this.layer.id].titleFontSize;
                }
            }
            return this.layer.titleFontSize || 14;
        },
        cueLineOpacity() {
            if (this.cueLineOpacityOptions[this.cue.id]) {
                return this.cueLineOpacityOptions[this.cue.id];
            }
            if (this.cue.lineOpacity) {
                return this.cue.lineOpacity;
            }
            
            return this.getCueLineOpacity(this.cue);
        },
        cueBoxOpacity() {
            if (this.cueBoxOpacityOptions[this.cue.id]) {
                return this.cueBoxOpacityOptions[this.cue.id];
            }
            if (this.cue.boxOpacity) {
                return this.cue.boxOpacity;
            }
            
            return this.getLayerBoxOpacity(this.cue);
        },
        notesListOpen() {
            // A1: open notes in Notes List (todo + review), sent as a snapshot every few minutes.
            return notesListOpenCount(NOTESLIST_INTEGRATION_ENABLED, this.project, this.notesListCounts, this.cue.id);
        },
        worknoteFromNotesList() {
            // One marker slot: an open Notes List note wins over an internal work note (purple over teal).
            return this.notesListOpen > 0;
        },
        isActiveCue() {
            const notesListOpen = this.notesListOpen;
            if (typeof workNoteOf(this.cue) === 'string') {
                return workNoteOf(this.cue) || notesListOpen || false;
            }
            const tempWorkNote = workNoteOf(this.cue).filter(item => !item.archived);
            return tempWorkNote.length + notesListOpen || false;
        },
        titleStyle() {
            return {
                fontSize: this.titleFontSize + 'px'
            };
        },
        descriptionStyle() {
            return {
                fontSize: this.titleFontSize + 'px'
            };
        },
        tetherWrapperStyle() {
            // const borderWidth = this.isIpad ? 7 : 8 - (((24 - this.titleFontSize) * 5) / 16);
            return {
                // width: `${12 * this.selectedScale}px !important`,
                width: `${(this.selectedScale < 1 ? (12 * (this.selectedScale / 2)) : 12)}px !important`,
                height: `${(this.selectedScale < 1 ? (12 * (this.selectedScale / 2)) : 12)}px !important`,
                backgroundColor: this.isFloatingCue ? this.layer.color : '#CCCCCC'
            };
        },
        tetherStyle() {
            const borderWidth = 8 - (((24 - this.titleFontSize) * 5) / 16);
            return {
                borderColor: this.isFloatingCue ? this.layer.color : '#CCCCCC',
                borderWidth: borderWidth + 'px'
            };
        },
        noteslistNoteIconSrc() {
            return noteslistNoteIcon;
        },
        noteslistNoteIconHoverSrc() {
            return noteslistNoteIconHover;
        },
        // Icon height = the cue box's own height, in explicit pixels.
        // Deliberately NOT a CSS percentage: .ql-cue-header-shell is auto-height,
        // so height:100% on an absolutely positioned child can resolve to auto
        // and collapse the icon to nothing. Measured instead, floored so a
        // not-yet-measured cue still renders something.
        noteslistIconSize() {
            // Prefer the measured cue box; fall back to the arithmetic cue.vue
            // uses for its height, so this is right even if the DOM read fails.
            const box = this.noteslistMeasuredHeight || (this.titleFontSize + 2);
            return Math.max(
                Math.round(box * NOTESLIST_ICON_SCALE),
                NOTESLIST_ICON_MIN
            );
        },
        // Cues can sit on either side of the page. When they are on the LEFT the
        // script — and the line joining the cue to its marker — is to the RIGHT
        // of the box, so an icon on that side lands on top of the connector.
        // Mirror it to the outside edge in that case.
        noteslistIconOnLeft() {
            // 'leftEdge' is margin view — the cue sits out in the left margin.
            // Both left variants put the script (and the connector line) to the
            // RIGHT of the box, so the icon belongs on the outer edge.
            const placement = this.getDefaultCuePlacement(this.cue.layer);
            return placement === 'left' || placement === 'leftEdge';
        },
        noteslistAddNoteStyles() {
            const h = this.noteslistIconSize;
            const gap = Math.max(
                Math.round(this.titleFontSize * NOTESLIST_GAP_RATIO),
                NOTESLIST_GAP_MIN
            );
            const styles = {
                height: h + 'px',
                width: Math.round(h * NOTESLIST_ICON_ASPECT) + 'px'
            };
            if (this.noteslistIconOnLeft) {
                styles.right = `calc(100% + ${gap}px)`;
                styles.left = 'auto';
            } else {
                styles.left = `calc(100% + ${gap}px)`;
                styles.right = 'auto';
            }
            return styles;
        },
        // Size of the note marker. Also drives how much room the title gives up
        // for it, so the two cannot drift apart -- see cueHeaderTitleStyles.
        worknoteMarkerSize() {
            return Math.max(this.titleFontSize - 4, 8);
        },
        worknoteStyles() {
            const size = this.worknoteMarkerSize + 'px';
            return {
                width: size,
                height: size,
                minWidth: size,
                minHeight: size
            };
        },
        cueHeaderTitleStyles() {
            const marginRight = Math.max(
                CUE_TITLE_GAP - (((24 - this.titleFontSize) * 6) / 16),
                CUE_TITLE_GAP_MIN
            );
            const styles = {
                marginRight: marginRight + 'px',
                maxWidth: `calc(100% - ${marginRight}px)`
            };
            // Reserve exactly the marker's width plus a fixed gap, less whatever
            // marginRight already contributes, so the mark sits WORKNOTE_MARKER_GAP
            // from the text whether the title is 12px or 24px.
            //
            // isActiveCue is the authority on "has a live note" and handles both
            // data shapes; the old code re-filtered cue.data.workNote here and would
            // throw on the legacy string form, which isActiveCue explicitly allows.
            if (this.isActiveCue) {
                const reserved =
                    (this.worknoteMarkerSize + WORKNOTE_MARKER_GAP) - marginRight;
                styles.paddingRight = `${reserved}px`;
            } else {
                styles.paddingRight = '0px';
            }

            return styles;
        },
        isIpad() {
            return /iPad|Macintosh/i.test(navigator.userAgent) && 'ontouchend' in document;
        }
    },
    methods: {
        // Read the cue header's rendered height so the Add Note icon can match it.
        // Cheap: one offsetHeight read, only for linked cues, on mount and when
        // anything that changes the cue's size changes.
        measureNoteslistIcon() {
            if (!this.notesListLinked) {
                return;
            }
            this.$nextTick(() => {
                const ref = this.$refs['ql-cue-header'];
                const el = ref && (ref.$el || ref);
                if (!el) {
                    return;
                }
                // The cue BOX is .ql-cue (height = titleFontSize + 2 in cue.vue);
                // the header toolbar inside it can be shorter. Measure the box,
                // falling back to the toolbar if it is not reachable.
                //
                // EXCEPT when the cue is expanded. .ql-cue then wraps the whole
                // cue-attributes body, so its offsetHeight is the PANEL, not the
                // header row -- and the icon came out the size of the panel
                // (~150px * 1.15). Measure .ql-cue-header-shell instead: it is the
                // header's own wrapper and the body is its sibling, so it stays the
                // header height whether the cue is open or shut.
                const box = el.closest ? el.closest('.ql-cue') : null;
                const expanded = !!(box && box.classList && box.classList.contains('cue-expanded'));
                const shell = el.closest ? el.closest('.ql-cue-header-shell') : null;
                const source = (expanded ? (shell || el) : (box || el));
                const h = source.offsetHeight;
                if (h && h !== this.noteslistMeasuredHeight) {
                    this.noteslistMeasuredHeight = h;
                }
            });
        },
        openAddNote() {
            return openNotesListAddNote({ project: this.project, user: this.user, cue: this.cue, toast: this.$toast });
        },
        focusInputWorkNote() {
            NavBus.$emit('cue-opened-with-right-click');
        },
        cuePlacementclass(cue) {
            const defaultCuePlacement = this.getDefaultCuePlacement(cue.layer);
            if (defaultCuePlacement === 'left' || defaultCuePlacement === 'right') {
                return `${defaultCuePlacement}-edge`;
            }
            return defaultCuePlacement;
        },
        toggle() {
            this.$emit('toggle');
        },
        selectLayer(layer) {
            if (this.locked) {
                return;
            }
            this.$props.cue.layer = layer;
            this.$store.dispatch('updateCue', this.$props.cue);
        },
        // Where in the field's text the click or tap landed, read before the text is
        // swapped for an input (see lib/cue-caret.js). null for Tab and other paths.
        caretFrom(e) {
            const point = eventPoint(e);
            if (!point || !e.currentTarget) return null;
            return caretOffsetAt(e.currentTarget, point.x, point.y, document);
        },
        // Focus the new input with the cursor at the click. A click focuses on the
        // next tick so early keystrokes aren't lost; a touch keeps the 100 ms wait
        // so the tap's own mouse events don't blur the input. If the user already
        // clicked into the input (a double-click), leave their cursor alone.
        focusField(ref, e, caret) {
            const delay = e && e.type === 'click' ? 0 : 100;
            this.$nextTick(() => {
                this.fitInputToText(this.$refs[ref] && this.$refs[ref].$el);
                setTimeout(() => {
                    const input = this.$refs[ref] && this.$refs[ref].$el;
                    if (!input || document.activeElement === input) return;
                    input.focus();
                    if (caret !== null) {
                        const at = Math.min(caret, input.value.length);
                        input.setSelectionRange(at, at);
                    }
                }, delay);
            });
        },
        selectWordInField(e) {
            const input = e.target;
            if (!input || input.tagName !== 'INPUT' || !this.$el.contains(input)) return;
            const [start, end] = wordBounds(input.value, input.selectionStart);
            input.setSelectionRange(start, end);
        },
        // The input is exactly as wide as its text (or its placeholder when empty),
        // like the text it replaced, and grows as you type, so it never scrolls
        // the text out of view.
        fitInputToText(input) {
            if (!input || input.dataset.fitted) return;
            input.dataset.fitted = '1';
            const ctx = document.createElement('canvas').getContext('2d');
            const fit = () => {
                const cs = getComputedStyle(input);
                ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
                const text = input.value || input.placeholder || '';
                const spacing = (parseFloat(cs.letterSpacing) || 0) * text.length;
                input.style.width = `${Math.ceil(ctx.measureText(text).width + spacing) + 2}px`;
            };
            fit();
            input.addEventListener('input', fit);
        },
        edit(e) {
            if (this.locked || !this.editable) {
                return;
            }
            if (this.isEditing || this.isEditingDescription) return;
            if (!this.headerBox.title) {
                this.editDescription(e);
                return;
            }
            const caret = this.caretFrom(e);
            this.$emit('updateStatus', {
                isEditing: true
            });
            this.focusField('input_cue_title', e, caret);
        },
        editDescription(e) {
            if (this.locked || !this.editable) {
                return;
            }
            if (e) {
                e.stopPropagation();
            }
            const caret = this.caretFrom(e);
            this.$emit('updateStatus', {
                isEditingDescription: true
            });
            this.focusField('input_cue_description', e, caret);
        },
        keyPress(e) {
            if (e.keyCode === 27) {
                this.$emit('updateStatus', {
                    isEditing: false
                });
                this.$props.cue.title = this.oldVal;
            }
        },
        keyDown(e) {
            if (e.key === 'Tab' && this.enabledHeaderFields().length >= 2) {
                e.preventDefault();
                this.tabFrom('title', e.target.value, 'title');
            }
            if (e.key === 'Enter') {
                this.modifyCue('title', e.target.value);
                e.preventDefault();
            }
            if (e.key === 'Escape') {
                this.$emit('updateStatus', {
                    isEditing: false
                });
                this.$refs.input_cue_title.$el.value = this.oldVal;
                this.$props.cue.title = this.oldVal;
                this.cue.title = this.oldVal;
            }
        },
        keyPressDescription(e) {
            if (e.keyCode === 27) {
                this.$emit('updateStatus', {
                    isEditingDescription: false
                });
                this.$props.cue.data.label = this.oldDescriptionVal;
            }
        },
        keyDownDescription(e) {
            if (e.key === 'Tab' && this.enabledHeaderFields().length >= 2) {
                e.preventDefault();
                this.tabFrom('label', e.target.value, 'data.label');
            }
            if (e.key === 'Enter') {
                this.modifyCue('data.label', e.target.value);
                e.preventDefault();
            }
            if (e.key === 'Escape') {
                this.$emit('updateStatus', {
                    isEditingDescription: false
                });
                this.$refs.input_cue_description.$el.value = this.oldDescriptionVal;
                this.$props.cue.data.label = this.oldDescriptionVal;
                this.cue.data.label = this.oldDescriptionVal;
            }
        },
        editTime(e) {
            if (this.locked || !this.editable) {
                return;
            }
            if (e) {
                e.stopPropagation();
            }
            const caret = this.caretFrom(e);
            this.$emit('updateStatus', {
                isEditingTime: true
            });
            this.focusField('input_cue_time', e, caret);
        },
        keyPressTime(e) {
            if (e.keyCode === 27) {
                this.$emit('updateStatus', {
                    isEditingTime: false
                });
                this.$props.cue.data.time = this.oldTimeVal;
            }
        },
        keyDownTime(e) {
            if (e.key === 'Tab' && this.enabledHeaderFields().length >= 2) {
                e.preventDefault();
                this.tabFrom('time', e.target.value, 'data.time');
            }
            if (e.key === 'Enter') {
                this.modifyCue('data.time', e.target.value);
                e.preventDefault();
            }
            if (e.key === 'Escape') {
                this.$emit('updateStatus', {
                    isEditingTime: false
                });
                this.$refs.input_cue_time.$el.value = this.oldTimeVal;
                this.$props.cue.data.time = this.oldTimeVal;
                this.cue.data.time = this.oldTimeVal;
            }
        },
        submitTime() {
            this.$emit('updateStatus', {
                isEditingTime: false
            });
        },
        // The header inline fields cycle title -> label -> time -> title, skipping
        // any that aren't visible (headerBox flags). Time is a peer here.
        enabledHeaderFields() {
            return ['title', 'label', 'time'].filter(f => this.headerBox[f]);
        },
        focusHeaderField(field) {
            if (field === 'title') {
                this.edit();
            } else if (field === 'label') {
                this.editDescription();
            } else if (field === 'time') {
                this.editTime();
            }
        },
        tabFrom(field, value, modifyKey) {
            const enabled = this.enabledHeaderFields();
            if (enabled.length < 2) {
                return;
            }
            const idx = enabled.indexOf(field);
            const next = enabled[(idx + 1) % enabled.length];
            this.modifyCue(modifyKey, value).then(() => {
                this.focusHeaderField(next);
            });
        },
        submit() {
            this.$emit('updateStatus', {
                isEditing: false
            });
        },
        submitDescription() {
            this.$emit('updateStatus', {
                isEditingDescription: false
            });
        },
        updateCue(cue) {
            this.$store.dispatch('updateCue', cue);
        },
        updateCueTitle() {
            this.$emit('updateStatus', {
                isEditing: false
            });
            this.oldVal = this.$props.cue.title;
            this.$store.dispatch('updateCue', this.cue);
        },
        updateCueDescription() {
            this.$emit('updateStatus', {
                isEditingDescription: false
            });
            this.oldDescriptionVal = this.$props.cue.data.label;
            this.$store.dispatch('updateCue', this.cue);
        },
        modifyCue(key, value, check = false) {
            return new Promise(async (resolve, reject) => {
                if (this.updateCueTimerId) {
                    clearTimeout(this.updateCueTimerId);
                }
                this.updateCueTimerId = setTimeout(() => {
                    if (value !== undefined) {
                        window.objectPath.set(this.cue, key, value);
                    }
                    if (check) {
                        setTimeout(() => {
                            const focusedElement = document.activeElement;
                            if (!focusedElement.classList.contains('ql-cue-layer-picker-button')) {
                                this.$emit('updateStatus', {
                                    isEditing: false,
                                    isEditingDescription: false,
                                    isEditingTime: false
                                });
                            }
                        }, 0);
                    } else {
                        this.$emit('updateStatus', {
                            isEditing: false,
                            isEditingDescription: false,
                            isEditingTime: false
                        });
                    }
                    this.oldVal = this.$props.cue.title;
                    this.oldDescriptionVal = this.$props.cue.data.label;
                    this.oldTimeVal = this.$props.cue.data.time;

                    this.$store.dispatch('modifyCue', {
                        id: this.cue.id,
                        data: {
                            [key]: value || window.objectPath.get(this.cue, key)
                        }
                    }).then(() => {
                        resolve();
                    }).catch(() => {
                        reject();
                    });
                }, 10);
            });
        },
        untetherCue() {
            if (this.editable) {
                let cueTopPos = 0;
                const header = this.$refs['ql-cue-header'].$el;
                const parent = header.closest('.ql-cue');
                if (parent) {
                    cueTopPos = window.getComputedStyle(parent).getPropertyValue('top');
                }
                cueTopPos = cueTopPos.replace('px', '');
                
                cueTopPos /= this.selectedScale;
                const defaultCuePlacement = this.getDefaultCuePlacement(this.cue.layer);
                
                let unTetherLeft = 0;
                if (defaultCuePlacement === 'left') {
                    unTetherLeft = `${2 * this.selectedScale}`;
                }
                if (defaultCuePlacement === 'right') {
                    const currentCueWidth = document.getElementById(this.cue.id).offsetWidth;
                    unTetherLeft = `${(this.svgContentWidth / this.selectedScale) - currentCueWidth}`;
                }
                if (defaultCuePlacement === 'leftEdge') {
                    const currentCueWidth = document.getElementById(this.cue.id).offsetWidth;
                    unTetherLeft = `-${(currentCueWidth + 10)}`;
                }
                if (defaultCuePlacement === 'rightEdge') {
                    // const currentCueWidth = document.getElementById(this.cue.id).offsetWidth;
                    unTetherLeft = `${(this.svgContentWidth / this.selectedScale) + 10}`;
                }
                this.$store.dispatch('addFloatingCue', {
                    id: this.cue.id,
                    left: unTetherLeft,
                    top: Math.max(cueTopPos, 0)
                });
                this.$emit('untetherCue');
            }
        },
        preventContext(e) {
            if (this.editable) {
                this.$store.dispatch('removeCueFloating', {
                    id: this.cue.id
                });
                this.$emit('tetherCue');
            }
            e.preventDefault();
            e.stopPropagation();
            return false;
        },
        checkTwoFingerTap(event) {
            this.lastClickCount++;
            if (this.lastClickCount === 2) {
                if (typeof event.touches !== 'undefined') {
                    event.preventDefault();
                    event = event.touches[0];
                }
                if (this.isIpad && event.target.className === 'move-button drag fill-button-retether') {
                    this.preventContext(event);
                }
                this.lastClickCount = 0;
            }
        },
        preventContextFunc: function(e) {
            e.preventDefault();
            e.stopPropagation();
            if (!this.editable) return;
            this.$emit('toggle');
            if (!this.notesListLinked) {
                this.$emit('focusWorkNote');
            }
        }
    },
    mounted() {
        this.measureNoteslistIcon();
    },
    watch: {
        titleFontSize: 'measureNoteslistIcon',
        notesListLinked: 'measureNoteslistIcon',
        // headerBox changes which fields render (number / label / time), which
        // changes the header's height — so the icon must re-measure with it.
        headerBox: 'measureNoteslistIcon'
    },
    components: {
        'layer-picker': ProjectCuesLayerPicker
    }
};

export default ProjectCuesCueHeader;
</script>

<style lang="scss">
.ql-cue-header-shell {
    position: relative;
    display: block;
    width: 100%;

    &--noteslist {
        // No width reserved on purpose: the Add Note icon is positioned past the
        // cue's right edge (left: calc(100% + gap)) and .ql-cue--noteslist-linked
        // keeps overflow visible, so a linked cue is exactly as wide as any other.
        // The devs' original reserved 88px here for a text pill.
        margin-right: 0;
    }
}

/* Add Note — the Notes List action on a linked cue.
   Deliberately OUTSIDE the cue box, as the devs had it: inside .ql-cue-header
   the toolbar is min-height:0 / overflow:hidden, so any child taller than the
   cue's line box grows the toolbar and clips the cue text — which caps an inside
   icon at an unusably small size. Out here nothing constrains it.
   Size/gap come from NOTESLIST_ICON_SIZE / NOTESLIST_ICON_GAP; the shell's
   reserved margin below is derived from the same constants, so changing the icon
   size cannot leave a stale reservation behind.
   Plain <button>, not <md-button>: nothing of vue-material's to override, so the
   ~18 !important declarations the old pill needed are gone. */
.noteslist-add-note-outside {
    position: absolute;
    // positioning context for the two stacked artworks
    // Match the cue box height exactly. Stretched between top:0 and bottom:0
    // rather than height:100% on purpose: .ql-cue-header-shell has AUTO height,
    // so a percentage height on an absolutely positioned child can resolve to
    // auto — and with line-height:0 there is no intrinsic height either, so the
    // button and the icon inside it collapse to nothing. Anchoring both edges
    // makes the height definite without needing to measure anything.
    top: 50%;
    transform: translateY(-50%);
    z-index: 5;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    box-sizing: border-box;
    margin: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    line-height: 0;

    // Hover is a change of ARTWORK, not a background wash. The mark's card is
    // opaque, so a background behind it was invisible anyway — and a coloured
    // halo around a card-shaped icon read as bolted on. Both images are layered
    // and cross-faded: no src swap, so there is no fetch-on-first-hover flash.
    &:hover,
    &:focus-visible {
        .noteslist-add-note-icon {
            opacity: 0;
        }
        .noteslist-add-note-icon--hover {
            opacity: 1;
        }
    }
    &:focus {
        outline: none;
    }
    &:focus-visible {
        box-shadow: 0 0 0 2px rgba(113, 83, 162, 0.45);
    }
}
// Dark mode: dim the white card so it stops being the brightest thing on the
// page, and lift the hover wash so it still reads against the darker icon.
// The opacity here is the dial for dark-mode brightness — lower is dimmer.
// Dark mode: dim whichever artwork is showing, so the white card stops being the
// brightest thing on the page. The hover rules above still win on :hover, so the
// dimmed base fades to the (also dimmed) hover mark.
.md-theme-show-mode .noteslist-add-note-outside .noteslist-add-note-icon {
    opacity: 0.72;
}
.md-theme-show-mode .noteslist-add-note-outside .noteslist-add-note-icon--hover {
    opacity: 0;
}
.md-theme-show-mode .noteslist-add-note-outside:hover,
.md-theme-show-mode .noteslist-add-note-outside:focus-visible {
    .noteslist-add-note-icon {
        opacity: 0;
    }
    .noteslist-add-note-icon--hover {
        opacity: 0.85;
    }
}
.noteslist-add-note-icon {
    // The button carries explicit px dimensions (see noteslistAddNoteStyles);
    // the images simply fill it. No percentage resolution against an
    // auto-height ancestor, so they cannot collapse.
    // Stacked in the same place so hover is a cross-fade. object-fit keeps each
    // mark's own proportions — the two artworks differ slightly in viewBox
    // (32.69x29.61 vs 29.56x27.78), so neither is stretched to match the other.
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    pointer-events: none;
    transition: opacity 0.12s ease;
}
.noteslist-add-note-icon--hover {
    opacity: 0;
}


.ql-cue-header {
    &.md-toolbar {
        min-height: 0px;
        overflow: hidden;
    }
    padding: 0;
    flex-wrap: nowrap;
    form.margin-view {
        min-width: 100px;
    }
    &.ql-cue-worknote-marked {
        .ql-cue-worknote-marker {
            display: block;
        }
        /* No padding-right here on purpose. It used to be a flat 25px and was
         * always overridden by the inline style from cueHeaderTitleStyles, which
         * is the only thing that knows the marker's actual size. Putting a number
         * back here would be dead code that looks authoritative. */
    }

    .cue-header-side-actions {
        position: relative;
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        align-self: stretch;
        z-index: 3;
        // Matches the tether handle's own 12px (see tetherWrapperStyle), which
        // also scales down with selectedScale. Was 16px, i.e. 4px of slack the
        // handle never used, charged to every cue's right edge.
        min-width: 12px;
    }

    .ql-cue-layer-func {
        cursor: pointer;
        width: 5px;
        min-width: 5px;
        position: relative;
        .ql-cue-layer-func-back {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            height: 72px;
            left: 0;
            width: 100%;
        }
    }

    .cue-header-title {
        position: relative;
        margin: 0 6px 0 0;
        flex: 1;
        min-width: 0;
        max-width: calc(100% - 18px);
        font-size: 14px;
        font-weight: 400;
        display: flex;
        align-items: stretch;
        line-height: 1.1;

        &.cue-header-both {
            .cue-header-cue-title {
                flex: unset;
                &.short-box {
                    min-width: min-content;
                }
            }
            .cue-header-cue-description {
                flex: unset;
                &.short-box {
                    min-width: min-content;
                }
            }
            .cue-header-cue-time {
                flex: unset;
                &.short-box {
                    min-width: min-content;
                }
            }
        }

        .cue-header-layer-title {
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            flex: 0 0 auto;
        }
        .cue-header-separator {
            align-self: stretch;
            flex: 0 0 1px;
            width: 1px;
            background: #aaa;
            margin: 0 8px;
        }
        // Name hidden: keep the dividing line before the first field, but drop its LEFT
        // margin so the ⋮ trigger's clickable padding runs right up to the line (no dead gap).
        .cue-header-separator--empty {
            margin-left: 0;
            margin-right: 4px;
        }
        .cue-header-cue-title {
            flex: 1 0 0;
            position: relative;
            font-weight: 100;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            &.placeholder {
                font-size: 16px;
                color: #999;
            }
            min-width: calc(min(40px, 10px));
        }
        .cue-header-cue-description {
            flex: 1 0 0;
            position: relative;
            font-weight: 100;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            z-index: 2;
            &.placeholder {
                font-size: 16px;
                color: #999;
            }
            min-width: calc(min(40px, 10px));
        }
        .cue-header-cue-time {
            flex: 1 0 0;
            position: relative;
            font-weight: 100;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            z-index: 2;
            &.placeholder {
                font-size: 16px;
                color: #999;
            }
            min-width: calc(min(40px, 10px));
        }
    }
    > .md-icon-button {
        width: 32px;
        height: 32px;
        margin: 0;
        min-width: 32px;
    }
    .ql-cue-worknote-marker {
        display: none;
        overflow: hidden;
        position: absolute;
        right: 0px;
        top: 50%;
        transform: translateY(-50%);
        transform-origin: 100% 50%;
        z-index: 1;
        /* Fixed colours per source, not the layer colour (Josh, 9 Sept 2026): a layer can be any hue, so
         * a purple layer would pass for a Notes List note. Teal = Cuelist work note, purple = Notes List. */
        color: var(--accent-ink);
        &.ql-cue-worknote-marker--noteslist {
            color: #7153a2;
        }
    }

    // The input that replaces a cue's number, label or time takes the field's own
    // size (titleStyle on the form), line height and no padding, and its width is
    // set to its text (fitInputToText), so the text doesn't move when you click in.
    // !important beats the app-wide "#app form { width: 100% }", and the 100px
    // floor the margin-view form needed when it replaced all three fields.
    .cue-header-title form {
        flex: 0 0 auto;
        width: auto !important;
        min-width: 0 !important;
    }
    .cue-header-title-edit, .cue-header-description-edit, .cue-header-time-edit {
        font-size: inherit;
        min-height: 0 !important;
        width: auto;
        margin: 0;
        padding: 0;
    }
    .cue-header-title input.md-input {
        font-size: inherit !important;
        letter-spacing: inherit;
        height: auto;
        line-height: 1.1;
        padding: 0;
        flex: 0 0 auto;
        // fitInputToText adds 2px so the cursor shows after the last letter;
        // take it back so the fields after this one don't shift.
        margin-right: -2px;
    }
    // An empty field's sample text ("Cue Label") stays the size and gray it is
    // at rest; the input-wide 16px and lighter #ccc made it overflow the box.
    .cue-header-title input.md-input::placeholder {
        font-size: inherit !important;
        color: #999 !important;
        -webkit-text-fill-color: #999 !important;
        opacity: 1;
    }

    .cue-header-side-actions .button-container {
        width: 16px;
        height: 16px;
        overflow: hidden;
        display: flex;
        cursor: pointer;
        position: absolute;
        right: 0;
        top: 0;
        transform: translate(50%, -50%) rotate(45deg);
        .move-button {
            display: block;
            text-align: center;
            .fill-button {
                width: 0;
                height: 0;
                border: solid 8px transparent;
            }
        }
    }
}

.md-theme-default.md-toolbar.ql-cue-header {
    background-color: white;
    color: black;
    .cue-header-title {
        background-color: white;
        color: black;
        input {
            font-size: 14px;
            font-weight: 100;
            background-color: white !important;
            color: black !important;
            -webkit-text-fill-color: black !important;
            &::-webkit-input-placeholder {
                color: #ccc;
            }
        }
    }
}

.md-theme-show-mode.md-toolbar.ql-cue-header {
    background-color: #424242;
    color: white;
    .ql-cue-worknote-marker.ql-cue-worknote-marker--noteslist {
        color: #c7bce2;
    }
    .cue-header-title {
        background-color: #424242;
        color: white;
        input {
            font-size: 14px;
            font-weight: 100;
            background-color: #424242 !important;
            color: white !important;
            -webkit-text-fill-color: white !important;
        }
        .md-input-focused:after {
            background-color: rgba(255, 255, 255, 0.6) !important;
        }
        .cue-header-separator {
            background: #999;
        }
    }
}
</style>


// WEBPACK FOOTER //
// cue-header.vue?4b0d6e70